import { defineStore } from "pinia";
import { RouteLocationNormalizedLoaded, Router, useRoute, useRouter } from "vue-router";
import z, { ZodError } from "zod";
import { getPollQuote } from "../services/api";
import { ensureDefined } from "../utils/types";
import { useConfigStore } from "../stores/config";
import { AnnuitiesPremium, PolicyType } from "../entities/premium";
import { Affiliate, QuoteRequest } from "../entities/quote";
import { mapDataFromQuoteRequest } from "../utils/quote";
import Cookies from "universal-cookie";
import { SCOTTISH_WIDOWS_ERROR_STATUS_CODE } from "@/constants/companyNames";
import { getSessionId } from "@/utils";
import { captureException } from "@sentry/vue";
const cookies = new Cookies();

export enum QuoteState {
    Ready,
    None,
    Loading,
}

export type QuoteToken = {
    token: string,
    quoteNumber: string,
}

type QuoteStore = {
    tokens: QuoteToken[],
    route: RouteLocationNormalizedLoaded,
    router: Router,
    panelSize: number,
    quotations: Array<AnnuitiesPremium>,
    quote: QuoteRequest | null,
    timeout: number,
    pollInterval: number,
    token: string,
    state: QuoteState,
    finished: boolean,
    pollingQuoteNumber: string | null,
    premiumRequest: { key: string, promise: Promise<AnnuitiesPremium> } | null,
}

export const useQuoteStore = defineStore("quoteStore", {
    state: (): QuoteStore => {
        return {
            tokens: [],
            route: useRoute(),
            router: useRouter(),
            panelSize: 0,
            quotations: [],
            quote: null,
            timeout: 60000,
            pollInterval: 1000,
            token: "",
            state: QuoteState.None,
            finished: false,
            pollingQuoteNumber: null,
            premiumRequest: null,
        };
    },
    actions: {
        getAffiliate(): Affiliate {
            let affiliate: Affiliate = {
                name: "AnnuityReady",
                number: 228,
                sourceId: null,
            };

            if (this.quote?.affiliate) {
                affiliate = { ...this.quote.affiliate };
            } else {
                const name = cookies.get("annuityaffname");
                const number = cookies.get("annuityaffnumber");
                if (name && number) {
                    affiliate = {
                        name: String(name),
                        number: Number(number),
                        sourceId: null,
                    };
                    if (this.quote) {
                        this.quote.affiliate = affiliate;
                    }
                }
            }

            const source = cookies.get("annuityaffsource");
            if (source) {
                affiliate.source = String(source);
            }

            return affiliate;
        },
        setToken(quoteNumber: string, token: string) {
            const config = useConfigStore();
            const cookie = this.parseCookie();
            if (!cookie.tokens) {
                cookie.tokens = [];
            }

            // Adds new token to the beginning of the tokens array
            cookie.tokens = [
                {
                    token,
                    quoteNumber,
                },
                ...cookie.tokens.slice(0, 5),
            ];
            this.tokens = cookie.tokens;
            cookies.set(config.sessionCookieKey, cookie, {
                secure: true,
            });
        },
        parseCookie() {
            const config = useConfigStore();
            const maybeCookie = cookies.get(config.sessionCookieKey);

            if (!maybeCookie) {
                return {};
            }
            return z.object({
                tokens: z.array(z.object({
                    token: z.string(),
                    quoteNumber: z.string(),
                })).optional(),
                myQuotesToken: z.object({
                    quoteNumber: z.string(),
                    token: z.string(),
                }).optional(),
            }).parse(maybeCookie);
        },
        getLatestQuoteToken() {
            if (!this.tokens?.length) {
                this.tokens = this.parseCookie().tokens ?? [];
            }
            const token = this.tokens[0];

            return token ?? null;
        },
        latestTokenByQuoteNumber(quoteNumber?: string | undefined) {
            if (!this.tokens?.length) {
                this.tokens = this.parseCookie().tokens ?? [];
            }

            if (quoteNumber) {
                const token = this.tokens.find(token => token.quoteNumber === quoteNumber);
                if (!token) {
                    throw new Error("Can't find token from provided quoteNumber");
                }
                return token;
            }
            // First token in array is the latest one.
            const token = this.tokens[0];

            if (!token) {
                throw new Error("Can't find token");
            }

            return token;
        },
        async getQuote(quoteToken: QuoteToken) {
            const config = useConfigStore();
            // Retrieve quote from quote-api
            const retrieved = await getPollQuote(
                { apiURL: config.apiUrl },
                `/v1/annuities/quote/${quoteToken.quoteNumber}?sessionId=${getSessionId()}`,
                quoteToken.token,
            );

            if (retrieved instanceof ZodError) {
                throw retrieved;
            }

            if ("errors" in retrieved && retrieved.errors) {
                throw new Error(`Unable to load quote: ${retrieved.errors[0].message}`);
            }

            if ("response" in retrieved) {
                const { finished, quote, quotations } = ensureDefined(retrieved.response);

                const quotationsToAdd = quotations.filter(quotation => !this.quotations.find(p => {
                    return p.premiumId === quotation.premiumId && p.company.slug === quotation.company.slug;
                }));

                this.quotations = [...this.quotations, ...quotationsToAdd];
                this.quote = ensureDefined(quote);
                this.finished = finished;
            }
        },
        async pollQuotes(quoteNumber: string | undefined) {
            try {
                if (!quoteNumber) {
                    throw new Error("Cannot retrieve a quote without a reference.");
                }

                if (this.state === QuoteState.Loading && this.pollingQuoteNumber === quoteNumber) {
                    return; // already polling this quote number
                }

                this.pollingQuoteNumber = quoteNumber;

                this.state = QuoteState.Loading;
                const quoteToken = this.latestTokenByQuoteNumber(quoteNumber);
                const finish = Date.now() + this.timeout;
                while (Date.now() < finish) {
                    await this.getQuote(quoteToken);

                    if (this.finished) {
                        this.state = QuoteState.Ready;
                        this.pollingQuoteNumber = null;
                        const definiteQuote = ensureDefined(this.quote);
                        mapDataFromQuoteRequest({
                            device: ensureDefined(definiteQuote.device),
                            affiliate: ensureDefined(definiteQuote.affiliate),
                            customer: ensureDefined(definiteQuote.customer),
                            product: ensureDefined(definiteQuote.product),
                            dependant: definiteQuote.dependant || {},
                        });
                        this.addScottishWidowsPremiumToResults();
                        return;
                    }

                    if (this.pollingQuoteNumber !== quoteNumber) {
                        return; // we don't care about this quote number any more
                    }

                    await new Promise(resolve => setTimeout(resolve, this.pollInterval));
                }
                throw new Error(`Unable to load quote: ${this.timeout}ms timeout exceeded`);
            } catch (err) {
                this.state = QuoteState.Ready;
                this.pollingQuoteNumber = null;
                this.router.push({ name: "error" });
                throw err;
            }
        },
        async getPremium(quoteNumber: string | undefined, premiumId: string | undefined) {
            const key = `${quoteNumber}-${premiumId}`;
            if (this.premiumRequest?.key === key) {
                return this.premiumRequest.promise;
            }

            const request = (async () => {
                try {
                    if (!premiumId) {
                        const error = new Error("Cannot find premium without a premium ID");
                        captureException(error, { extra: { quoteNumber, premiumId } });
                        throw error;
                    }

                    const quoteToken = this.latestTokenByQuoteNumber(quoteNumber);
                    await this.getQuote(quoteToken);
                    const premium = this.quotations.find(x => x.premiumId === +premiumId);

                    if (!premium) {
                        const error = new Error(`Cannot find premium with premium ID: ${premiumId}`);
                        captureException(error, { extra: { quoteNumber, premiumId } });
                        throw error;
                    }

                    return premium;
                } catch (err) {
                    this.router.push({ name: "error" });
                    captureException(err, { extra: { quoteNumber, premiumId } });
                    throw err;
                } finally {
                    this.premiumRequest = null;
                }
            })();

            this.premiumRequest = { key, promise: request };
            return request;
        },

        addScottishWidowsPremiumToResults() {
            const scottishWidowsNoPremiumReturned = !this.quotations.find(premium => premium.company.slug === "sw");
            if (scottishWidowsNoPremiumReturned) {
                this.quotations.push({
                    premiumId: 0,
                    company: {
                        name: "Scottish Widows",
                        slug: "sw",
                    },
                    policy: {
                        type: PolicyType.Error,
                        statusCode: SCOTTISH_WIDOWS_ERROR_STATUS_CODE,
                        totalAnnualPension: 0,
                        error: true,
                    },
                    documents: {
                        illustration: null,
                        termsConditions: null,
                        keyFeatures: null,
                    },
                    datapoints: {
                        description: "",
                    },
                });
            }
        },
    },
});
